import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from '../../../graphql/client';
import { BULK_UPDATE_ROUTE_STOPS_DELIVERY_INFO } from '../../../graphql/requests/bulkUpdateRouteStopsDeliveryInfo';
import {
  GBulkUpdateRouteStopsDeliveryInfoInput,
  GBulkUpdateRouteStopsDeliveryInfoPayload,
  GRouteError,
} from '../../../graphql/types/generated';
import analytics from '../../../services/analytics';
import { getErrorCode } from '../../../utils';
import { hideModal, setIsModalLoading } from '../modal';
import { NotificationType, showNotification } from '../notification';
import { getAllStopStatuses } from './getAllStopStatues';
import { getPrimaryStopStatuses } from './getPrimaryStopStatuses';

export const bulkUpdateRouteStopsDeliveryInfo = createAsyncThunk<
  GBulkUpdateRouteStopsDeliveryInfoPayload | null,
  GBulkUpdateRouteStopsDeliveryInfoInput & {
    warehouseId: string;
    nightEnd: string;
  }
>(
  'delivery/bulkUpdateRouteStopsDeliveryInfo',
  async (
    input: GBulkUpdateRouteStopsDeliveryInfoInput & {
      warehouseId: string;
      nightEnd: string;
    },
    thunkAPI,
  ): Promise<GBulkUpdateRouteStopsDeliveryInfoPayload | null> => {
    thunkAPI.dispatch(setIsModalLoading(true));
    const { data, error } = await client
      .mutation(BULK_UPDATE_ROUTE_STOPS_DELIVERY_INFO, {
        input: { deliveryRouteId: input.deliveryRouteId },
      })
      .toPromise();
    thunkAPI.dispatch(setIsModalLoading(false));
    thunkAPI.dispatch(hideModal());

    const mutationResult = data?.bulkUpdateRouteStopsDeliveryInfo;
    const userErrors: GRouteError[] = mutationResult?.userErrors ?? [];
    const bulkUpdateDeprecatedError = userErrors.find(
      (userError) => userError.__typename === 'BulkUpdateDeprecated',
    );

    if (error || !mutationResult || userErrors.length > 0) {
      analytics.track('Bulk Update Route Stops Delivery Info Failed', {
        routeId: input.deliveryRouteId,
      });
      thunkAPI.dispatch(
        showNotification({
          type: NotificationType.Error,
          message:
            bulkUpdateDeprecatedError?.message ??
            'Failed to mark route as delivered. Please try again.',
          errorCode:
            bulkUpdateDeprecatedError?.errorCode ??
            getErrorCode({
              error,
              expectedData: mutationResult?.route,
              userErrors:
                userErrors.length > 0
                  ? userErrors.map((userError) => ({
                      __typename: userError.__typename ?? 'UnknownUserError',
                    }))
                  : undefined,
            }).trim(),
        }),
      );
      return null;
    }
    analytics.track('Bulk Update Route Stops Delivery Info', {
      routeId: input.deliveryRouteId,
    });
    thunkAPI.dispatch(
      showNotification({
        type: NotificationType.Success,
        message: 'All stops on the route have been marked as delivered!',
      }),
    );
    thunkAPI.dispatch(
      getAllStopStatuses({
        warehouseId: input.warehouseId,
        nightEnd: input.nightEnd,
      }),
    );
    thunkAPI.dispatch(
      getPrimaryStopStatuses({
        warehouseId: input.warehouseId,
        nightEnd: input.nightEnd,
      }),
    );
    return mutationResult;
  },
);
